/**
 * The front page's moving parts: the lift picker, whose figure lights what one set of a lift
 * counts toward, and the icons. Everything drawn here comes from the app's own outlines,
 * icons and numbers.
 */
import { BACK_POLYGONS, FRONT_POLYGONS, VIEW_HEIGHT, VIEW_WIDTH, litPart, tones } from '../kit/bodyPolygons'
import { BACK_SLUG, FRONT_SLUG } from '../kit/bodySlugs'
import { ICONS, type IconName } from '../kit/iconData'
import { MUSCLES, type MuscleId } from '../science/muscles'
import { startCounting } from '../lib/count'
import { LANDING_LIFTS, type LandingLift } from './landingData'

startCounting('site')

const SVG_NS = 'http://www.w3.org/2000/svg'
const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
let drawn = 0

const esc = (text: string) => text.replaceAll('&', '&amp;').replaceAll('<', '&lt;')

function iconSvg(name: IconName): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="butt" stroke-linejoin="miter" aria-hidden="true" focusable="false">${ICONS[name].replaceAll('{id}', `lp-i${++drawn}`)}</svg>`
}

/** Each muscle's shapes: every shape whole in its shaded tone, and its lit part over it. */
type Figure = Map<MuscleId, { shape: SVGPolygonElement; lit: boolean }[]>

/**
 * The front and back figure from the app's outlines, each muscle's shapes kept to colour, and
 * over them the thin line of the page's colour the app draws around every shape.
 */
function drawFigure(host: Element): Figure {
  const figure: Figure = new Map()
  const unlit = tones('var(--heat-0)')
  for (const [polygons, slugs] of [[FRONT_POLYGONS, FRONT_SLUG], [BACK_POLYGONS, BACK_SLUG]] as const) {
    const svg = document.createElementNS(SVG_NS, 'svg')
    svg.setAttribute('viewBox', `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`)
    svg.setAttribute('aria-hidden', 'true')
    for (const [slug, list] of Object.entries(polygons)) {
      const muscle = slugs[slug]
      for (const points of list) {
        const parts: [string, boolean][] = [[points, false]]
        const light = litPart(points)
        if (light) parts.push([light, true])
        for (const [part, lit] of parts) {
          const shape = document.createElementNS(SVG_NS, 'polygon')
          shape.setAttribute('points', part)
          shape.style.fill = unlit[lit ? 0 : 1]
          if (muscle) figure.set(muscle, [...(figure.get(muscle) ?? []), { shape, lit }])
          svg.append(shape)
        }
      }
    }
    const edge = document.createElementNS(SVG_NS, 'path')
    edge.setAttribute('class', 'lp-edge')
    edge.setAttribute('d', Object.values(polygons).flat().map((points) => `M${points}Z`).join(''))
    svg.append(edge)
    host.append(svg)
  }
  return figure
}

function paint(figure: Figure, fill: (m: MuscleId) => string): void {
  for (const [muscle, shapes] of figure) {
    const [lit, shaded] = tones(fill(muscle))
    for (const { shape, lit: isLit } of shapes) shape.style.fill = isLit ? lit : shaded
  }
}

/** A colour mixed into the unlit body, as the app shades its figures. */
const tint = (colour: string, percent: number) => `color-mix(in srgb, ${colour} ${Math.round(percent)}%, var(--heat-0))`

// What one set counts toward: the lift picker, its figure and its rows. The figure rises into
// place unlit and then lights, so the first thing it does is show the count.
const demo = document.querySelector('[data-demo]')
const demoFigure = demo?.querySelector('[data-figure="lift"]')
const demoName = demo?.querySelector('[data-lift-name]')
const demoRows = demo?.querySelector('[data-rows]')
if (demo && demoFigure && demoName && demoRows) {
  const figure = drawFigure(demoFigure)
  const chips = [...demo.querySelectorAll<HTMLButtonElement>('[data-lift]')]
  const show = (lift: LandingLift) => {
    const role = new Map(lift.roles)
    paint(figure, (m) => (role.get(m) === 'direct' ? 'var(--accent)' : role.get(m) === 'indirect' ? tint('var(--accent)', 42) : 'var(--heat-0)'))
    if (demoName.textContent !== lift.name) demoName.textContent = lift.name
    demoRows.innerHTML = lift.roles.map(([muscle, r]) => `<li class="lp-row"><i class="lp-swatch${r === 'direct' ? '' : ' lp-swatch--help'}"></i><span class="lp-row__name">${esc(MUSCLES[muscle].name)}</span><span class="lp-row__num">${r === 'direct' ? '1' : '½'}</span></li>`).join('')
    for (const chip of chips) chip.setAttribute('aria-pressed', String(chip.dataset.lift === lift.id))
  }
  let picked = false
  for (const chip of chips) {
    chip.addEventListener('click', () => {
      const lift = LANDING_LIFTS.find((l) => l.id === chip.dataset.lift)
      if (lift) { picked = true; show(lift) }
    })
  }
  const first = LANDING_LIFTS[0]
  if (first) {
    if (still) show(first)
    else setTimeout(() => { if (!picked) show(first) }, 500)
  }
}

for (const el of document.querySelectorAll<HTMLElement>('[data-icon]')) {
  const name = el.dataset.icon
  if (name && name in ICONS) el.innerHTML = iconSvg(name as IconName)
}
